import { useState } from 'react';
import { Star, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { aPorcentaje, type ResultadoBusqueda } from '../services/tariff-catalog-service';
import { agruparResultados, textoPropio, type GrupoCapitulo } from '../utils/agrupar-resultados';
import { aplanarNiveles } from '../utils/niveles-partida';
import { partirResaltando, aclaracionDeVocabulario } from '../utils/resaltado';

/**
 * Los resultados dentro de su rama del arancel (tarea C1, vault 127).
 *
 * La tabla plana ordenaba bien pero no explicaba nada: quince filas seguidas,
 * varias diciendo "Los demás", sin manera de ver que las tres primeras son la
 * misma partida de cuatro dígitos y que las de abajo son de otro capítulo que
 * apenas menciona el producto. El arancel es un árbol y se lee como árbol.
 */

interface Props {
  resultados: ResultadoBusqueda[];
  /** Los términos con los que se buscó, para marcar por qué apareció cada fila. */
  terminos: string[];
  /** Los pares {de, a} de la IA, para aclarar el vocabulario fila por fila. */
  traducciones?: Array<{ de: string; a: string }>;
  /**
   * La partida mejor rankeada de la búsqueda ENTERA, para la estrella.
   *
   * 🔴 No se puede deducir de `resultados`: cuando hay clarificación, los
   * mejores candidatos se muestran arriba en botones y acá llega el resto.
   * Estrellar el primero de lo que llegó le pone "la que mejor encaja" a la
   * tercera — pasó, y se ve igual de convincente que la verdadera. Si el mejor
   * no está en el árbol, no va estrella.
   */
  idMejor?: string;
  onAbrir: (code: string) => void;
  /**
   * Abre el capitulo entero en el arbol del libro (frente C2 del plan 178).
   * Opcional: el que no lo pasa simplemente no muestra el boton.
   */
  onVerCapitulo?: (capitulo: string) => void;
}

const pct = (v: string | number) => `${aPorcentaje(v)}%`;

export function ResultadosArbol({
  resultados,
  terminos,
  traducciones,
  idMejor,
  onAbrir,
  onVerCapitulo,
}: Props) {
  const { principales, otrasMenciones, ningunoEsLaCosa } = agruparResultados(resultados);
  const totalOtras = otrasMenciones.reduce((n, c) => n + c.total, 0);

  return (
    <div className="space-y-3">
      {/* Ninguna partida quedó como coincidencia plena y lo que se muestra son
          las más cercanas. Callarlo las haría pasar por seguras, y una partida
          mal elegida se paga en aduana. */}
      {ningunoEsLaCosa && (
        <div className="flex items-start gap-2 rounded-md border border-alerta/40 bg-alerta/5 px-3 py-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-alerta " />
          <span className="text-xs text-muted-foreground">
            Ninguna encaja de lleno con lo que buscaste. Estas son las que más se le acercan —
            revisalas con cuidado antes de elegir.
          </span>
        </div>
      )}

      {principales.map((c) => (
        <Capitulo
          key={c.capitulo}
          grupo={c}
          idMejor={idMejor}
          terminos={terminos}
          traducciones={traducciones}
          onAbrir={onAbrir}
          onVerCapitulo={onVerCapitulo}
        />
      ))}

      {/* Los capítulos que solo HABLAN del producto (betún para calzado,
          máquinas para fabricarlo). Plegados, no escondidos: a veces el
          producto del usuario es justamente el betún. */}
      {totalOtras > 0 && (
        <Plegable
          titulo={`Otras menciones (${totalOtras})`}
          sub="Capítulos que hablan de lo que buscaste, sin ser eso"
        >
          <div className="space-y-3 pt-3">
            {otrasMenciones.map((c) => (
              <Capitulo
                key={c.capitulo}
                grupo={c}
                idMejor={undefined}
                terminos={terminos}
                traducciones={traducciones}
                onAbrir={onAbrir}
                onVerCapitulo={onVerCapitulo}
              />
            ))}
          </div>
        </Plegable>
      )}
    </div>
  );
}

function Plegable({
  titulo,
  sub,
  children,
}: {
  titulo: string;
  sub: string;
  children: React.ReactNode;
}) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center gap-2 p-3 text-left"
      >
        {abierto ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">{titulo}</span>
        <span className="text-xs text-muted-foreground">— {sub}</span>
      </button>
      {abierto && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function Capitulo({
  grupo,
  idMejor,
  terminos,
  traducciones,
  onAbrir,
  onVerCapitulo,
}: {
  grupo: GrupoCapitulo;
  idMejor?: string;
  terminos: string[];
  traducciones?: Array<{ de: string; a: string }>;
  onAbrir: (code: string) => void;
  onVerCapitulo?: (capitulo: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <div className="flex items-baseline gap-2 border-b bg-muted/40 px-3 py-2">
        <span className="font-mono text-xs font-bold">Cap. {grupo.capitulo}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {grupo.nombre}
        </span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {grupo.total} {grupo.total === 1 ? 'partida' : 'partidas'}
        </span>
        {onVerCapitulo && (
          <button
            type="button"
            onClick={() => onVerCapitulo(grupo.capitulo)}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
          >
            Ver capítulo completo
          </button>
        )}
      </div>

      {grupo.partidas.map((p) => (
        <div key={p.codigo}>
          {/* El nivel de 4 dígitos: es texto del libro, no una hoja. Sin
              alícuota y en gris, como en el navegador por capítulo — si
              llevara alícuota se elegiría una rama creyendo elegir una
              partida. */}
          <TituloPartida codigo={p.codigo} titulo={p.titulo} />
          {/* Dentro del grupo, el libro: niveles intermedios con sus guiones y
              las hojas en orden de código (frente C1 del plan 178). El ranking
              ya decidió el orden de capítulos y grupos, y la estrella sigue
              marcando la mejor de la búsqueda entera. */}
          {aplanarNiveles(p.filas).map((item, i) =>
            item.tipo === 'nivel' ? (
              <div
                key={`nivel-${item.code ?? item.texto}-${i}`}
                className="flex items-baseline gap-2 border-b bg-background px-3 py-1.5"
              >
                <span className="w-[110px] shrink-0 font-mono text-[11px] text-muted-foreground">
                  {item.code ?? ''}
                </span>
                <span className="min-w-0 flex-1 text-xs italic text-muted-foreground">
                  <span className="mr-1 opacity-60">{'—'.repeat(item.nivel)}</span>
                  {item.texto}
                </span>
              </div>
            ) : (
              <Fila
                key={`${item.fila.id}-${item.fila.origen}`}
                fila={item.fila}
                nivel={item.nivel}
                esMejor={item.fila.id === idMejor}
                terminos={terminos}
                traducciones={traducciones}
                onAbrir={onAbrir}
              />
            )
          )}
        </div>
      ))}
    </div>
  );
}

function Fila({
  fila,
  nivel = 1,
  esMejor,
  terminos,
  traducciones,
  onAbrir,
}: {
  fila: ResultadoBusqueda;
  /** Profundidad en el libro: cuántos guiones lleva delante. */
  nivel?: number;
  esMejor: boolean;
  terminos: string[];
  traducciones?: Array<{ de: string; a: string }>;
  onAbrir: (code: string) => void;
}) {
  const propio = textoPropio(fila);
  // El primer término es el sustantivo núcleo (el backend le da doble peso en
  // el consenso). Su traducción se salta acá: la comparten todas las filas.
  const aclaracion = aclaracionDeVocabulario(propio, traducciones, terminos[0]);

  return (
    <button
      type="button"
      onClick={() => onAbrir(fila.code)}
      className="flex w-full items-start gap-3 border-b px-3 py-2 text-left last:border-b-0 transition-colors hover:bg-primary/5"
    >
      <span className="w-[110px] shrink-0 pt-0.5 font-mono text-xs font-semibold">{fila.code}</span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm">
          {/* Los guiones son los del arancel impreso: la profundidad se lee
              igual acá que en el libro, sin sangría extra que desalinee la
              columna de códigos. */}
          <span className="mr-1 text-xs text-muted-foreground/70">{'—'.repeat(nivel)}</span>
          {esMejor && (
            <Star
              className="mr-1 inline h-3.5 w-3.5 -translate-y-px fill-alerta text-alerta"
              aria-label="la que mejor encaja"
            />
          )}
          {partirResaltando(propio, terminos).map((t, i) =>
            t.resaltado ? (
              <mark key={i} className="rounded-sm bg-primary/15 px-0.5 text-foreground">
                {t.texto}
              </mark>
            ) : (
              <span key={i}>{t.texto}</span>
            )
          )}
        </span>

        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {esMejor && <span className="text-xs font-medium text-alerta ">la que mejor encaja</span>}
          {/* La partida EXCLUYE algo que se pidió. No se saca de la rama: el
              usuario tiene que poder ver que existe y por qué no es la suya. */}
          {fila.niega === true && (
            <span className="inline-flex items-center gap-1 text-xs text-alerta ">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              dice “sin”, no “con”
            </span>
          )}
          {/* Lo que evita que el usuario descarte la partida correcta por no
              reconocer cómo la nombra el arancel. */}
          {aclaracion && (
            <span className="text-xs text-muted-foreground">
              esto es lo que vos llamás “{aclaracion}”
            </span>
          )}
          {fila.origen === 'APERTURA' && (
            <span className="text-xs text-muted-foreground">
              tu apertura, cuelga de {fila.codigoOficialPadre}
            </span>
          )}
          {fila.viaTermino && (
            <span className="text-xs text-muted-foreground">
              por tu sinónimo “{fila.viaTermino}”
            </span>
          )}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
        {fila.importProhibited === true && (
          <Badge variant="destructive" title="Esta mercadería no puede importarse a Bolivia">
            No importable
          </Badge>
        )}
        {fila.hasIce === true && <Badge variant="destructive">ICE</Badge>}
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          GA {pct(fila.gaRate)} · IVA {pct(fila.ivaRate)}
        </span>
      </span>
    </button>
  );
}

/** A partir de acá el título del libro se recorta al primer tramo (C3, plan 178). */
const TITULO_LARGO = 90;

/**
 * El encabezado de la partida de 4 dígitos. El libro enumera con `;`: el primer
 * tramo alcanza para reconocer la rama («Pigmentos, opacificantes y colores
 * preparados…») y el resto se abre a pedido. Sin el recorte, la 3207 ocupa
 * cuatro renglones de encabezado antes del primer resultado.
 */
function TituloPartida({ codigo, titulo }: { codigo: string; titulo: string }) {
  const [expandido, setExpandido] = useState(false);
  const primerTramo = titulo.split(';')[0];
  const recortable = titulo.length > TITULO_LARGO && primerTramo.length < titulo.length;
  const visible = recortable && !expandido ? `${primerTramo}…` : titulo;
  return (
    <div className="flex items-baseline gap-2 border-b bg-background px-3 py-1.5">
      <span className="w-[110px] shrink-0 font-mono text-[11px] text-muted-foreground">
        {codigo}
      </span>
      <span className="min-w-0 flex-1 text-xs italic text-muted-foreground">{visible}</span>
      {recortable && (
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          className="shrink-0 text-[11px] text-primary hover:underline"
        >
          {expandido ? 'menos' : 'ver todo'}
        </button>
      )}
    </div>
  );
}
